/**
 * Memory monitoring utilities for the Crate Hackers app
 * Helps track memory usage and identify potential memory leaks
 */

// Memory thresholds (in MB)
const MEMORY_THRESHOLDS = {
  TRACK_ARRAY_WARNING: 50, // Warn if track arrays exceed 50MB
  STATE_OBJECT_WARNING: 100, // Warn if any state object exceeds 100MB
  TOTAL_HEAP_WARNING: 1024, // Warn if total heap exceeds 1GB
};

/**
 * Estimates the memory size of a JavaScript object in bytes
 */
export const estimateObjectSize = (obj: any): number => {
  const seen = new WeakSet();

  const calculateSize = (value: any): number => {
    if (value === null || value === undefined) return 0;

    if (typeof value === 'boolean') return 4;
    if (typeof value === 'number') return 8;
    if (typeof value === 'string') return value.length * 2; // UTF-16

    if (typeof value === 'object') {
      if (seen.has(value)) return 0; // Avoid circular references
      seen.add(value);

      let size = 0;
      if (Array.isArray(value)) {
        size += value.reduce((acc, item) => acc + calculateSize(item), 0);
      } else {
        for (const key in value) {
          if (value.hasOwnProperty(key)) {
            size += key.length * 2; // Key size
            size += calculateSize(value[key]); // Value size
          }
        }
      }
      return size;
    }

    return 0;
  };

  return calculateSize(obj);
};

/**
 * Converts bytes to human-readable format
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Monitor memory usage of track arrays and other large objects
 */
export const monitorStateMemory = (
  stateName: string,
  stateValue: any,
): void => {
  try {
    const sizeBytes = estimateObjectSize(stateValue);
    const sizeMB = sizeBytes / (1024 * 1024);

    if (sizeMB > MEMORY_THRESHOLDS.TRACK_ARRAY_WARNING) {
      console.warn(
        `⚠️ Memory Warning: ${stateName} is using ${formatBytes(sizeBytes)} (${sizeMB.toFixed(2)} MB)`,
        {
          stateName,
          size: formatBytes(sizeBytes),
          itemCount: Array.isArray(stateValue) ? stateValue.length : 'N/A',
          recommendation:
            'Consider implementing pagination or virtual scrolling',
        },
      );
    } else {
      console.log(
        `📊 Memory Monitor: ${stateName} is using ${formatBytes(sizeBytes)}`,
      );
    }
  } catch (error) {
    console.warn('Error monitoring memory for', stateName, error);
  }
};

/**
 * Get memory performance information (browser only)
 */
export const getMemoryInfo = (): any => {
  if (
    typeof window !== 'undefined' &&
    'performance' in window &&
    'memory' in (window as any).performance
  ) {
    const memory = (window as any).performance.memory;
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      usedMB: memory.usedJSHeapSize / (1024 * 1024),
      totalMB: memory.totalJSHeapSize / (1024 * 1024),
      limitMB: memory.jsHeapSizeLimit / (1024 * 1024),
    };
  }
  return null;
};

/**
 * Log comprehensive memory status
 */
export const logMemoryStatus = (): void => {
  const memInfo = getMemoryInfo();

  if (memInfo) {
    console.group('🧠 Memory Status Report');
    console.log(
      `Used Heap: ${formatBytes(memInfo.usedJSHeapSize)} (${memInfo.usedMB.toFixed(2)} MB)`,
    );
    console.log(
      `Total Heap: ${formatBytes(memInfo.totalJSHeapSize)} (${memInfo.totalMB.toFixed(2)} MB)`,
    );
    console.log(
      `Heap Limit: ${formatBytes(memInfo.jsHeapSizeLimit)} (${memInfo.limitMB.toFixed(2)} MB)`,
    );

    if (memInfo.usedMB > MEMORY_THRESHOLDS.TOTAL_HEAP_WARNING) {
      console.warn(
        '⚠️ High memory usage detected! Consider running cleanup operations.',
      );
    } else {
      console.log('✅ Memory usage is within normal limits');
    }

    console.groupEnd();
  } else {
    console.log('📊 Memory monitoring not available in this environment');
  }
};

/**
 * React hook for memory monitoring in development
 */
export const useMemoryMonitor = (
  stateName: string,
  stateValue: any,
  enabled = true,
) => {
  if (process.env.NODE_ENV === 'development' && enabled) {
    // Only monitor in development to avoid production overhead
    React.useEffect(() => {
      if (
        stateValue &&
        (Array.isArray(stateValue) || typeof stateValue === 'object')
      ) {
        monitorStateMemory(stateName, stateValue);
      }
    }, [stateName, stateValue]);
  }
};

// Add React import for the hook
declare const React: any;
